import { useState, useEffect, useCallback } from 'react';
import { getCreditBalance } from '../utils/creditsApi';
import { logger } from '../utils/logger';

/**
 * Hook for managing user credits
 * @param {Object|null} user - Firebase user object
 * @returns {{credits: number|null, isAdmin: boolean, loading: boolean, error: string|null, refreshCredits: Function}}
 */
export function useCredits(user) {
  const [credits, setCredits] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCredits = useCallback(async () => {
    if (!user) {
      setCredits(null);
      setIsAdmin(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await getCreditBalance(user);
      setCredits(data.balance);
      setIsAdmin(Boolean(data.is_admin));
    } catch (err) {
      logger.error('Failed to fetch credits:', err);
      setError(err.message);
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Fetch on mount and when user changes
  useEffect(() => {
    fetchCredits();
  }, [fetchCredits]);

  // Refresh function for external use (e.g., after purchase)
  const refreshCredits = useCallback(() => {
    fetchCredits();
  }, [fetchCredits]);

  return {
    credits,
    isAdmin,
    loading,
    error,
    refreshCredits,
  };
}
